import { action, computed, makeObservable, observable, runInAction } from "mobx"

import { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { SCALE } from "@/domains/tokens/static-tokens"

export type IncentiveType = "vote" | "lp"

// PairAddress param sources following the multi-source pattern
export interface PairAddressParamSources {
  urlParam: string | undefined
  localstorage: string | undefined
  default: string
  ui: string | undefined
}

// TokenAddress param sources
export interface TokenAddressParamSources {
  urlParam: string | undefined
  localstorage: string | undefined
  default: string
  ui: string | undefined
}

// IncentiveType param sources
export interface IncentiveTypeParamSources {
  urlParam: IncentiveType | undefined
  localstorage: IncentiveType | undefined
  default: IncentiveType
  ui: IncentiveType | undefined
}

export class IncentivesPageStore {
  pairAddressParamSources: PairAddressParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "",
    ui: undefined,
  }

  tokenAddressParamSources: TokenAddressParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: SCALE.address.toString(),
    ui: undefined,
  }

  incentiveTypeParamSources: IncentiveTypeParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "vote",
    ui: undefined,
  }

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private chainSlug: string,
  ) {
    makeObservable(this, {
      pairAddressParamSources: observable,
      tokenAddressParamSources: observable,
      incentiveTypeParamSources: observable,
      pairAddress: computed,
      tokenAddress: computed,
      incentiveType: computed,
      setPairAddressFromUrlParam: action,
      setPairAddressFromLocalStorage: action,
      setPairAddressFromUi: action,
      setTokenAddressFromUrlParam: action,
      setTokenAddressFromLocalStorage: action,
      setTokenAddressFromUi: action,
      setIncentiveTypeFromUrlParam: action,
      setIncentiveTypeFromLocalStorage: action,
      setIncentiveTypeFromUi: action,
    })
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  private initializeFromStorage(): void {
    // Initialize pairAddress from localStorage
    const localPairAddress = this.storage.getItem(`${this.chainSlug}-incentivize-pairAddress`)
    if (localPairAddress) {
      this.setPairAddressFromLocalStorage(localPairAddress)
    }

    // Initialize tokenAddress from localStorage
    const localTokenAddress = this.storage.getItem(`${this.chainSlug}-incentivize-tokenAddress`)
    if (localTokenAddress) {
      this.setTokenAddressFromLocalStorage(localTokenAddress)
    }

    // Initialize incentiveType from localStorage
    const localIncentiveType = this.storage.getItem(`${this.chainSlug}-incentivize-incentiveType`)
    if (localIncentiveType && (localIncentiveType === "vote" || localIncentiveType === "lp")) {
      this.setIncentiveTypeFromLocalStorage(localIncentiveType as IncentiveType)
    }
  }

  public initializeFromUrlParams(): void {
    // Initialize pairAddress from URL params
    const urlPairAddress = this.urlParams.getParam("pairAddress")
    if (urlPairAddress) {
      this.setPairAddressFromUrlParam(urlPairAddress)
    }

    // Initialize tokenAddress from URL params
    const urlTokenAddress = this.urlParams.getParam("tokenAddress")
    if (urlTokenAddress) {
      this.setTokenAddressFromUrlParam(urlTokenAddress)
    }

    // Initialize incentiveType from URL params
    const urlIncentiveType = this.urlParams.getParam("incentiveType")
    if (urlIncentiveType && (urlIncentiveType === "vote" || urlIncentiveType === "lp")) {
      this.setIncentiveTypeFromUrlParam(urlIncentiveType as IncentiveType)
    }
  }

  get pairAddress(): string {
    return (
      this.pairAddressParamSources.ui ??
      this.pairAddressParamSources.urlParam ??
      this.pairAddressParamSources.localstorage ??
      this.pairAddressParamSources.default
    )
  }

  get tokenAddress(): string {
    return (
      this.tokenAddressParamSources.ui ??
      this.tokenAddressParamSources.urlParam ??
      this.tokenAddressParamSources.localstorage ??
      this.tokenAddressParamSources.default
    )
  }

  get incentiveType(): IncentiveType {
    return (
      this.incentiveTypeParamSources.ui ??
      this.incentiveTypeParamSources.urlParam ??
      this.incentiveTypeParamSources.localstorage ??
      this.incentiveTypeParamSources.default
    )
  }

  setPairAddressFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.pairAddressParamSources.urlParam === value) return
    this.pairAddressParamSources.urlParam = value
  }

  setPairAddressFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.pairAddressParamSources.localstorage === value) return
    this.pairAddressParamSources.localstorage = value
  }

  setPairAddressFromUi(value: string | undefined): void {
    if (value === undefined) return
    if (this.pairAddressParamSources.ui === value) return
    this.pairAddressParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-incentivize-pairAddress`, value)
    this.urlParams.setParam("pairAddress", value)
  }

  setTokenAddressFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.tokenAddressParamSources.urlParam === value) return
    this.tokenAddressParamSources.urlParam = value
  }

  setTokenAddressFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.tokenAddressParamSources.localstorage === value) return
    this.tokenAddressParamSources.localstorage = value
  }

  setTokenAddressFromUi(value: string | undefined): void {
    if (value === undefined) return
    if (this.tokenAddressParamSources.ui === value) return
    this.tokenAddressParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-incentivize-tokenAddress`, value)
    this.urlParams.setParam("tokenAddress", value)
  }

  setIncentiveTypeFromUrlParam(value: IncentiveType | undefined): void {
    if (!value) return
    if (value !== "vote" && value !== "lp") return
    if (this.incentiveTypeParamSources.urlParam === value) return
    this.incentiveTypeParamSources.urlParam = value
  }

  setIncentiveTypeFromLocalStorage(value: IncentiveType | undefined): void {
    if (!value) return
    if (value !== "vote" && value !== "lp") return
    if (this.incentiveTypeParamSources.localstorage === value) return
    this.incentiveTypeParamSources.localstorage = value
  }

  setIncentiveTypeFromUi(value: IncentiveType | undefined): void {
    if (value === undefined) return
    if (value !== "vote" && value !== "lp") return
    if (this.incentiveTypeParamSources.ui === value) return
    this.incentiveTypeParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(`${this.chainSlug}-incentivize-incentiveType`, value)
    this.urlParams.setParam("incentiveType", value)
  }
}

